import { Button, Col, Form, Input, Row } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SHORT_DATE_TIME_FORMAT, disabledTime } from '../../../utils/date';
import { StyledAlert, StyledDatePicker, StyledFormItem, Title } from './style';

const { TextArea } = Input;

interface Props {
  onCreate: Function;
  onCancel: Function;
  isCustomAPIKey: boolean;
}

const AddAPIKeyInstanceForm = ({
  onCreate,
  onCancel,
  isCustomAPIKey,
}: Props) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const initialValues = {
    activeFrom: null,
    expiresAt: null,
    apiKey: null,
  };
  const [visible, setVisible] = useState(isCustomAPIKey ? 1 : 0);
  const disabledDate = (current: any) => {
    let disabledDates = false;

    if (current) {
      if (form.getFieldValue('activeFrom')) {
        disabledDates =
          current <= moment(form.getFieldValue('activeFrom')).endOf('day') ||
          current <= moment().startOf('day');
      } else {
        disabledDates = current <= moment().endOf('day');
      }
    }

    return disabledDates;
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onFinish={async values => {
        try {
          await onCreate(values);
        } catch {
          form.setFields([
            {
              name: 'apiKey',
              errors: [
                intl.formatMessage({
                  id: 'ApiKeys.modal.input.validation.isApiKeyAlreadyExists',
                }),
              ],
            },
          ]);
        }
      }}
    >
      {(isCustomAPIKey ? visible === 1 : true) && (
        <Title>
          <FormattedMessage id="ApiKeys.modal.title" />
        </Title>
      )}
      {(isCustomAPIKey ? visible === 2 : false) && (
        <Title>
          <FormattedMessage id="ApiKeys.GenerateApikeyModal.title" />
        </Title>
      )}
      <Row justify="center" gutter={[10, 0]}>
        <Col span={12}>
          <StyledFormItem
            label={<FormattedMessage id="ApiKeys.modal.label.activefrom" />}
            name="activeFrom"
            validateFirst
            hidden={isCustomAPIKey && visible === 2}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'ApiKeys.modal.required.ActiveFrom',
                }),
              },
              {
                validator: async (_rule, value) => {
                  const expiresAt = form.getFieldValue('expiresAt');
                  if (
                    expiresAt &&
                    new Date(value).getTime() > new Date(expiresAt).getTime()
                  ) {
                    throw new Error(
                      'Active from should be less than Active until',
                    );
                  }
                },
              },
            ]}
          >
            <StyledDatePicker
              data-testid="api-key-instance_activeFrom"
              format={SHORT_DATE_TIME_FORMAT}
              showTime
            />
          </StyledFormItem>
        </Col>
        <Col span={12}>
          <StyledFormItem
            label={<FormattedMessage id="ApiKeys.modal.label.activeuntil" />}
            name="expiresAt"
            validateFirst
            hidden={isCustomAPIKey && visible === 2}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'ApiKeys.modal.required.ActiveUntil',
                }),
              },
              {
                validator: async (_rule, value) => {
                  if (new Date(value).getTime() < new Date().getTime()) {
                    throw new Error(
                      'Active until should be a future date and time',
                    );
                  }
                },
              },
            ]}
          >
            <StyledDatePicker
              format={SHORT_DATE_TIME_FORMAT}
              data-testid="api-key-instance_activeUntil"
              showTime
              disabledDate={disabledDate}
              disabledTime={disabledTime}
              onChange={() => {
                form.validateFields(['activeFrom']);
              }}
            />
          </StyledFormItem>
        </Col>
      </Row>
      {(isCustomAPIKey ? visible === 2 : false) && (
        <Row justify="center" gutter={[15, 0]} style={{ marginTop: '20px' }}>
          <Col span={24}>
            <StyledFormItem
              className="custom-apikey"
              name="apiKey"
              label={<FormattedMessage id="ApiKeys.modal.label.customApikey" />}
              validateFirst
              rules={[
                {
                  validateTrigger: 'onSubmit',
                  validator: (_rule, value) => {
                    const regexResult = value?.match(/^[a-zA-Z0-9-]*$/);
                    const lengthResult = value?.length < 20;
                    if (regexResult === null && lengthResult) {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: 'ApiKeys.modal.input.validation.maxCharOnlyalphanumeric',
                          }),
                        ),
                      );
                    }

                    if (value === undefined || value === null || lengthResult) {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: 'ApiKeys.modal.input.validation.maxCharacters',
                          }),
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
                {
                  pattern: new RegExp('^[a-zA-Z0-9-]*$'),
                  message: (
                    <FormattedMessage id="ApiKeys.modal.input.validation.onlyAlphanumeric" />
                  ),
                },
              ]}
            >
              <TextArea
                rows={2}
                showCount
                maxLength={128}
                data-testid="apikeyinput"
              />
            </StyledFormItem>
            <ul className="required-input">
              <li>
                <FormattedMessage id="ApiKeys.modal.input.helpText.maxCharacters" />
              </li>
              <li>
                <FormattedMessage id="ApiKeys.modal.input.helpText.onlyAlphanumeric" />
              </li>
            </ul>
          </Col>
          <Col span={24}>
            <StyledAlert
              message={
                <FormattedMessage id="ApiKeys.modal.notification.apikeySecret" />
              }
              type="info"
              showIcon
            />
          </Col>
        </Row>
      )}

      <Row justify="center" gutter={[15, 0]} style={{ marginTop: '20px' }}>
        <Col>
          <Button
            type="default"
            shape="round"
            size="large"
            onClick={() => onCancel()}
          >
            <FormattedMessage id="Global.cancel" />
          </Button>
        </Col>
        <Col>
          {(!isCustomAPIKey || visible === 2) && (
            <Button
              type="primary"
              shape="round"
              size="large"
              htmlType="submit"
              data-testid="custom-apikey"
            >
              <FormattedMessage id="ApiKeys.modal.btn.create" />
            </Button>
          )}

          {isCustomAPIKey && visible === 1 && (
            <Button
              type="primary"
              shape="round"
              size="large"
              onClick={() => {
                form.validateFields(['activeFrom', 'expiresAt']);
                const activeFrom = form.getFieldValue('activeFrom');
                const expiresAt = form.getFieldValue('expiresAt');
                if (activeFrom && expiresAt) {
                  setVisible(2);
                }
              }}
              data-testid="custom-apikey-next"
            >
              <FormattedMessage id="Global.next" />
            </Button>
          )}
        </Col>
      </Row>
    </Form>
  );
};

export default AddAPIKeyInstanceForm;
